import { useEffect, useRef } from 'react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { filterUndefineds } from '@olivierpascal/helpers';
import * as Sentry from '@sentry/browser';
import { Stack } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { isAxiosError } from 'axios';
import { useLocation } from 'react-router-dom';

import type { IContainerProps } from '~/components/utils/Container';
import { ApiError, useApi } from '~/lib/api';

export type IErrorMessageSeverityLevel =
  | 'fatal'
  | 'error'
  | 'warning'
  | 'log'
  | 'info'
  | 'debug';

export type IErrorMessageProps = IContainerProps & {
  error?: unknown;
  severityLevel?: IErrorMessageSeverityLevel;
  children?: React.ReactNode;
};

const styles = stylex.create({
  host: {
    color: colorSchemeTokens.error,
  },
  stack: {
    borderLeftWidth: 4,
    borderLeftStyle: 'solid',
    borderLeftColor: colorSchemeTokens.error,
    paddingLeft: 12,
  },
  body: {
    fontSize: 12,
    whiteSpace: 'pre-wrap',
  },
  icon: {
    marginRight: '0.125rem',
  },
});

type IErrorInfo = {
  messages: Array<string>;
  context?: object;
  stack?: string;
  severityLevel: IErrorMessageSeverityLevel;
};

const getErrorInfo = (error: unknown): IErrorInfo | undefined => {
  if (isAxiosError(error)) {
    return {
      messages: [`${error.message} (code: ${error.code})`],
      stack: error.stack,
      severityLevel: 'error',
      context: error.response,
    };
  }

  if (error instanceof ApiError) {
    return {
      messages: error.errors.map((error) =>
        error.message ? `${error.message} (code: ${error.code})` : error.code,
      ),
      stack: error.stack,
      severityLevel: 'error',
      context: error.context,
    };
  }

  if (error instanceof Error) {
    return {
      messages: [error.message],
      stack: error.stack,
      severityLevel: 'error',
    };
  }

  if (typeof error === 'string') {
    return {
      messages: [error],
      severityLevel: 'info',
    };
  }

  return undefined;
};

export const ErrorMessage: React.FC<IErrorMessageProps> = (props) => {
  const { sx, children, error, severityLevel } = props;
  const location = useLocation();
  const errorInfo = getErrorInfo(error);
  const api = useApi();
  const userAuthorization = api.getAuthorization();
  const isDevelopment = process.env.NODE_ENV === 'development';

  const isInitRef = useRef(false);
  useEffect(() => {
    if (isInitRef.current) {
      return;
    }
    isInitRef.current = true;

    if (
      errorInfo &&
      ['fatal', 'error', 'warning'].includes(errorInfo.severityLevel)
    ) {
      const sentryCaptureContext = {
        level: severityLevel ?? errorInfo.severityLevel,
        contexts: {
          ...errorInfo.context,
        },
      };
      Sentry.captureException(error, sentryCaptureContext);
    }
  }, [location.pathname, errorInfo, userAuthorization, error, severityLevel]);

  const renderErrors = (messages: Array<React.ReactNode>): React.ReactNode => (
    <Stack as="ul" gap={1}>
      {messages.map((message, index) => (
        <li key={index}>
          <FontAwesomeIcon
            {...stylex.props(styles.icon)}
            icon={faTriangleExclamation}
          />
          {messages.length > 1 ? `${index + 1}/${messages.length}` : ''}{' '}
          {message}
        </li>
      ))}
    </Stack>
  );

  return (
    <Stack gap={4} sx={[styles.host, sx]}>
      {errorInfo?.messages
        ? renderErrors(filterUndefineds(errorInfo.messages))
        : null}

      {children ? <pre {...stylex.props(styles.body)}>{children}</pre> : null}

      {isDevelopment && errorInfo?.stack ? (
        <Stack gap={1}>
          <div>Stack trace (appears only in development mode):</div>
          <pre {...stylex.props(styles.body, styles.stack)}>
            {errorInfo.stack}
          </pre>
        </Stack>
      ) : null}
    </Stack>
  );
};
